import { useAppContext } from "../../app-context/use-app-context"
import { useFetchOptionsQuery, useFetchOrgOptionsQuery } from "../api"
import { getCustomerOptions, getMembershipOptions, getSupplierOptions, getWorkspaceOptions } from "../utils"

/**
 * @param orgId - (Optional) The organization ID to fetch options for. If not provided, the hook will attempt to use the org ID from the main context.
 * @returns An object containing:
 *   - `options`: The array of options relevant to the passed organisation or current context (user or organization).
 *   - `queryMetadata`: Metadata about the query, such as loading and error states.
 */
export const useOptions = (orgId?: string | undefined) => {
  const { appContext } = useAppContext()
  const { mainContext } = appContext
  const orgIdFromMainContext = mainContext?.type === "org" ? mainContext.id : undefined
  const orgIdQuery = orgId || orgIdFromMainContext
  const skipOrgOptionsQuery = !orgIdQuery

  const { data: options = [], ...optionsQueryMetadata } = useFetchOptionsQuery(undefined, {
    refetchOnMountOrArgChange: 1,
  })
  const { data: orgOptions = [], ...orgOptionsQueryMetadata } = useFetchOrgOptionsQuery(orgIdQuery || "", {
    skip: skipOrgOptionsQuery,
    refetchOnMountOrArgChange: 1,
  })

  const fallbackOrgOptions = orgIdQuery ? options.filter((option) => option.orgId === orgIdQuery) : []
  const queryMetadata = skipOrgOptionsQuery ? optionsQueryMetadata : orgOptionsQueryMetadata
  const mainContextOptions = skipOrgOptionsQuery
    ? options
    : orgOptionsQueryMetadata.isLoading
    ? fallbackOrgOptions
    : orgOptions
  const customerOptions = getCustomerOptions(mainContextOptions)
  const supplierOptions = getSupplierOptions(mainContextOptions)
  const membershipOptions = getMembershipOptions(mainContextOptions)
  const workspaceOptions = getWorkspaceOptions(mainContextOptions)

  return {
    options: mainContextOptions,
    customerOptions,
    supplierOptions,
    membershipOptions,
    workspaceOptions,
    queryMetadata,
  }
}
